/**
 * The public gallery uses one semantic palette. The legacy names below remain
 * as aliases while older admin and shared components finish their migration;
 * they intentionally point at the same values instead of defining another
 * visual system.
 */
const semanticColors = {
  background: '#1D1D1B',
  surface: '#272724',
  surfaceRaised: '#32322E',
  imageMat: '#11110F',
  textPrimary: '#F5F1E8',
  textSecondary: '#D0CBC0',
  textMuted: '#A9A398',
  border: 'rgba(245, 241, 232, 0.2)',
  borderStrong: 'rgba(245, 241, 232, 0.34)',
  focus: '#D9C57D',
  accent: '#B9A062',
  accentHover: '#D9C57D',
  onAccent: '#1D1D1B',
  error: '#E28B7E',
  success: '#88BA83',
  warning: '#EBC76B',
  info: '#9BC4D7',
} as const;

const legacyGray = {
  light: semanticColors.surfaceRaised,
  medium: semanticColors.textMuted,
  dark: semanticColors.textSecondary,
} as const;

export const theme = {
  colors: {
    semantic: semanticColors,

    // Legacy aliases. New code should use colors.semantic.*.
    primary: semanticColors.accentHover,
    secondary: semanticColors.textSecondary,
    background: semanticColors.background,
    text: semanticColors.textPrimary,
    white: semanticColors.surface,
    black: semanticColors.textPrimary,

    // Enhanced mobile palette
    mobile: {
      background: semanticColors.background,
      surface: semanticColors.surface,
      text: semanticColors.textPrimary,
      textSecondary: semanticColors.textSecondary,
      border: semanticColors.border,
      divider: 'rgba(245, 241, 232, 0.12)',
      overlay: 'rgba(17, 17, 15, 0.82)',
      focus: semanticColors.focus,
    },

    // Brand-aligned color scale (Inverted for Dark Mode)
    gray: {
      ...legacyGray,
    },

    // Semantic colors with better contrast
    error: semanticColors.error,
    success: semanticColors.success,
    warning: semanticColors.warning,
    info: semanticColors.info,
    primaryDark: semanticColors.textSecondary,
    primaryLight: semanticColors.textPrimary,
    successLight: '#B4D4A8',
    warningLight: '#F4DEA0',
    errorLight: '#F0B0A4',
    errorDark: '#B9625A',

    // Joanne Art Class gallery color system
    gallery: {
      background: semanticColors.background,
      surface: semanticColors.background,
      surfaceRaised: semanticColors.surface,
      surfaceElevated: semanticColors.surfaceRaised,
      surfaceVariant: semanticColors.surfaceRaised,
      mat: semanticColors.imageMat,
      border: {
        subtle: 'rgba(245, 241, 232, 0.12)',
        default: semanticColors.border,
        strong: 'rgba(217, 197, 125, 0.48)',
      },

      white: semanticColors.background,
      card: semanticColors.surface,
      secondary: semanticColors.surfaceRaised,
      charcoal: semanticColors.textPrimary,
      stormGrey: semanticColors.textSecondary,
      silver: semanticColors.textMuted,

      text: {
        primary: semanticColors.textPrimary,
        secondary: semanticColors.textSecondary,
        tertiary: semanticColors.textMuted,
        inverse: semanticColors.background,
        muted: semanticColors.textMuted,
        accent: semanticColors.accentHover,
      },

      neutrals: {
        lightest: semanticColors.surface,
        light: semanticColors.surfaceRaised,
        medium: '#6F6B62',
        dark: semanticColors.textSecondary,
        darkest: semanticColors.textPrimary,
      },

      accent: {
        primary: semanticColors.accent,
        secondary: semanticColors.accentHover,
        hover: semanticColors.accentHover,
        active: '#9B8247',
        border: 'rgba(185, 160, 98, 0.48)',
      },

      semantic: {
        error: semanticColors.error,
        warning: semanticColors.warning,
        success: semanticColors.success,
      },
    },
  },
  typography: {
    fontFamily: {
      heading: '"Fraunces", "Inter", serif',
      body: '"Inter", sans-serif',

      gallery: {
        display: '"Fraunces", "Inter", serif',
        heading: '"Fraunces", "Inter", serif',
        body: '"Inter", sans-serif',
        caption: '"Inter", sans-serif',
        monospace: '"SF Mono", "Monaco", "Cascadia Code", "Roboto Mono", monospace',
      },

      mobile: {
        system: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        readable: '"Inter", sans-serif',
        display: '"Fraunces", "Inter", serif',
      },
    },
    fontSize: {
      // Mobile-first typography with golden ratio scaling (1.618)
      // Base size: 16px on mobile, ensuring accessibility
      xs: '0.75rem',    // 12px - maintained for compatibility
      sm: '0.875rem',   // 14px - maintained for compatibility  
      base: '1rem',     // 16px - minimum mobile readable size
      lg: '1.125rem',   // 18px - maintained for compatibility
      xl: '1.25rem',    // 20px - maintained for compatibility
      '2xl': '1.5rem',  // 24px - maintained for compatibility
      '3xl': '1.875rem', // 30px - maintained for compatibility
      '4xl': '2.25rem', // 36px - maintained for compatibility
      '5xl': '3rem',    // 48px - maintained for compatibility

      // Golden ratio scaled typography system
      golden: {
        xs: '0.875rem',     // 14px (16 ÷ 1.143)
        sm: '1rem',         // 16px - base mobile size
        base: '1.125rem',   // 18px (16 × 1.125)
        md: '1.25rem',      // 20px (16 × 1.25)
        lg: '1.618rem',     // 25.9px (16 × golden ratio)
        xl: '2.023rem',     // 32.4px (16 × golden²)
        '2xl': '2.618rem',  // 41.9px (16 × golden² + golden)
        '3xl': '3.272rem',  // 52.4px (16 × golden³)
        '4xl': '4.236rem',  // 67.8px (16 × golden⁴)
        '5xl': '5.295rem',  // 84.7px (16 × golden⁵)
      },

      // Mobile-optimized gallery sizes
      gallery: {
        caption: '0.875rem',  // 14px - readable on mobile
        metadata: '1rem',     // 16px - standard mobile base
        title: '1.25rem',     // 20px - readable artwork titles
        section: '1.618rem',  // 26px - golden ratio section headings
        display: 'clamp(2rem, 4vw, 2.5rem)',
        hero: 'clamp(2.75rem, 6vw, 3.5rem)',
        massive: '3.5rem',
      },

      // Responsive scaling helpers
      mobile: {
        body: '1rem',         // 16px minimum for accessibility
        caption: '0.875rem',  // 14px for small text
        heading: '1.25rem',   // 20px for headings
        display: '1.618rem',  // 26px for mobile display
        hero: '2.023rem',     // 32px for mobile hero
      },
    },
    fontWeight: {
      // Existing weights - maintained for backward compatibility
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,

      // Gallery-specific font weights
      gallery: {
        light: 300,        // For elegant, airy text
        normal: 400,       // Standard body text
        medium: 500,       // Slightly emphasized
        semibold: 600,     // Section headings
        bold: 700,         // Strong emphasis
        extrabold: 800,    // Display headings
      },
    },

    // Gallery-specific line heights for optimal readability
    lineHeight: {
      tight: 1.1,    // For large display text
      snug: 1.25,    // For headings
      normal: 1.5,   // For body text
      relaxed: 1.625, // For longer content
      loose: 2,      // For captions with breathing room
    },

    mobile: {
      body: '1rem',
      heading: '1.25rem',
      caption: '0.875rem',
    },
  },
  spacing: {
    // 8-point grid system (mobile-first)
    // Ensures consistent, touch-friendly spacing
    xs: '0.25rem',  // 4px - maintained for compatibility
    sm: '0.5rem',   // 8px - base 8pt unit
    md: '1rem',     // 16px - standard spacing
    lg: '1.5rem',   // 24px - comfortable spacing  
    xl: '2rem',     // 32px - large spacing
    '2xl': '3rem',  // 48px - section spacing
    '3xl': '4rem',  // 64px - major spacing
    '4xl': '6rem',  // 96px - dramatic spacing

    // 8-point grid system units
    grid: {
      '1': '0.5rem',   // 8px - 1 grid unit
      '2': '1rem',     // 16px - 2 grid units  
      '3': '1.5rem',   // 24px - 3 grid units
      '4': '2rem',     // 32px - 4 grid units
      '5': '2.5rem',   // 40px - 5 grid units
      '6': '3rem',     // 48px - 6 grid units
      '8': '4rem',     // 64px - 8 grid units
      '10': '5rem',    // 80px - 10 grid units
      '12': '6rem',    // 96px - 12 grid units
      '16': '8rem',    // 128px - 16 grid units
      '20': '10rem',   // 160px - 20 grid units
    },

    // Mobile-optimized spacing with touch targets
    mobile: {
      micro: '0.25rem',    // 4px - fine details
      xs: '0.5rem',        // 8px - minimal spacing
      sm: '1rem',          // 16px - standard mobile spacing  
      md: '1.5rem',        // 24px - comfortable mobile spacing
      lg: '2rem',          // 32px - section spacing
      xl: '2.5rem',        // 40px - large section spacing
      touch: '2.75rem',    // 44px - minimum touch target
      gutter: '1rem',      // 16px - mobile gutter
      padding: '1rem',     // 16px - content padding
      margin: '1.5rem',    // 24px - content margins
    },

    // Gallery-specific professional spacing
    gallery: {
      micro: '0.25rem',    // 4px - fine details
      tiny: '0.5rem',      // 8px - minimal spacing
      xs: '1rem',          // 16px - small spacing
      sm: '1.5rem',        // 24px - comfortable spacing
      md: '2rem',          // 32px - section spacing  
      lg: '3rem',          // 48px - large section breaks
      xl: '4rem',          // 64px - major section divisions
      '2xl': '6rem',       // 96px - page-level spacing
      '3xl': '8rem',       // 128px - hero spacing
      '4xl': '10rem',      // 160px - dramatic spacing

      // Content-specific spacing
      artwork: {
        tight: '0.5rem',     // 8px - between related elements
        comfortable: '1rem', // 16px - standard artwork spacing
        generous: '2rem',    // 32px - between artwork cards
        hero: '3rem',        // 48px - hero artwork spacing
      },

      // Layout-specific spacing  
      layout: {
        gutter: '1rem',      // 16px - mobile gutter
        gutterLg: '2rem',    // 32px - desktop gutter
        margin: '1.5rem',    // 24px - content margins
        section: '3rem',     // 48px - between major sections
        container: '1rem',   // 16px - container padding
      },
    },
  },
  breakpoints: {
    // Mobile-first breakpoint system
    xs: '320px',      // Small phones
    sm: '375px',      // Large phones
    md: '414px',      // Large phones landscape
    lg: '768px',      // Tablets
    xl: '1024px',     // Small desktops
    '2xl': '1440px',  // Large desktops

    // Backward compatible aliases
    mobile: '320px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1440px',

    // Touch-friendly minimum sizes
    touchTarget: '44px',  // Minimum touch target size
    mobileGutter: '16px', // Minimum mobile spacing
  },
  layout: {
    // The fixed public header owns this offset for every non-home public page.
    headerHeight: '70px',
    headerOffset: 'calc(70px + env(safe-area-inset-top, 0px))',
    contentMaxWidth: '1200px',
  },
  borderRadius: {
    // Existing radius - maintained for backward compatibility
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',

    // Gallery-specific radius
    gallery: {
      none: '0',
      subtle: '0.125rem',    // 2px - Very subtle rounding
      soft: '0.375rem',      // 6px - Soft, professional corners
      comfortable: '0.5rem',  // 8px - Comfortable rounding
      prominent: '0.75rem',   // 12px - More pronounced
      dramatic: '1.25rem',    // 20px - Strong visual impact
    },
  },
  shadows: {
    // Existing shadows - maintained for backward compatibility
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',

    // Gallery-specific shadows
    gallery: {
      none: 'none',
      subtle: '0 1px 3px rgba(0, 0, 0, 0.24)',
      soft: '0 2px 8px rgba(0, 0, 0, 0.28)',
      comfortable: '0 4px 16px rgba(0, 0, 0, 0.34)',
      prominent: '0 8px 24px rgba(0, 0, 0, 0.4)',
      dramatic: '0 16px 48px rgba(0, 0, 0, 0.48)',

      // Art-specific shadows for framing effect
      artwork: {
        subtle: '0 4px 16px rgba(0, 0, 0, 0.32)',
        frame: '0 8px 24px rgba(0, 0, 0, 0.4)',
        museum: '0 14px 36px rgba(0, 0, 0, 0.48)',
      },

      // Interactive shadows
      hover: '0 12px 32px rgba(0, 0, 0, 0.45)',
      focus: '0 0 0 3px rgba(216, 185, 120, 0.28)',
    },
  },
  transitions: {
    // Existing transitions - maintained for backward compatibility
    fast: '150ms ease-in-out',
    normal: '250ms ease-in-out',
    slow: '350ms ease-in-out',

    // Gallery-specific luxury transitions
    gallery: {
      instant: '100ms ease-out',                           // For immediate feedback
      quick: '200ms cubic-bezier(0.4, 0, 0.2, 1)',       // For hover effects
      smooth: '400ms cubic-bezier(0.4, 0, 0.2, 1)',      // For smooth interactions
      elegant: '400ms cubic-bezier(0.4, 0, 0.2, 1)',
      dramatic: '400ms cubic-bezier(0.4, 0, 0.2, 1)',
      luxurious: '400ms cubic-bezier(0.4, 0, 0.2, 1)',

      // Specific use cases
      hover: '300ms cubic-bezier(0.4, 0, 0.2, 1)',       // Perfect for card hovers
      reveal: '400ms cubic-bezier(0.4, 0, 0.2, 1)',
      modal: '400ms cubic-bezier(0.4, 0, 0.2, 1)',       // For modal animations
    },
  },
};
